import type { FullscriptClient } from "@/lib/fullscript/client";
import { classifyFullscriptForbidden } from "@/lib/fullscript/forbidden";

/**
 * Returns the first Fullscript patient id that this Fullscript account can read.
 *
 * Fullscript keeps one patient record per clinic account, and each provider connects their own
 * account, so a member can hold one id per account (ENG-5823). Fullscript answers 404 for a
 * patient in another account, and can answer 403. Both mean "not this account", so the next id is
 * tried. A 403 that names a missing OAuth scope is not one: the token cannot read any patient, so
 * it is rethrown. Any other error is rethrown too: the caller fails closed and retries, and never
 * acts on an id it could not verify.
 *
 * @returns The readable id, or undefined when no stored id belongs to this account.
 */
export async function findReadableFullscriptPatientId({
  fullscript,
  fullscriptPatientIds,
}: {
  fullscript: FullscriptClient;
  fullscriptPatientIds: string[];
}): Promise<string | undefined> {
  for (const fullscriptPatientId of fullscriptPatientIds) {
    try {
      if (await fullscript.getPatient(fullscriptPatientId)) return fullscriptPatientId;
    } catch (error) {
      if (classifyFullscriptForbidden(error)?.kind === "forbidden_other") continue;
      throw error;
    }
  }
  return undefined;
}
